import React from 'react';
import ReactDOM from 'react-dom';
import SignatureCanvas from 'react-signature-canvas';
import { RotateCcw, Trash2 } from 'lucide-react';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onClear: () => void;
  onDelete?: () => void;
  title: string;
  signatureRef: React.RefObject<SignatureCanvas>;
  hasExistingSignature?: boolean;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onClear,
  onDelete,
  title,
  signatureRef,
  hasExistingSignature = false,
}) => {
  // Log modal rendering position and canvas dimensions
  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const canvas = signatureRef.current?.getCanvas();
        
        console.log('=== MODAL RENDERED ===');
        
        if (canvas) {
          const canvasRect = canvas.getBoundingClientRect();
          console.log('Canvas info:', {
            internalWidth: canvas.width,
            internalHeight: canvas.height,
            visualWidth: canvasRect.width,
            visualHeight: canvasRect.height,
            computedStyle: {
              width: window.getComputedStyle(canvas).width,
              height: window.getComputedStyle(canvas).height
            }
          });
          
          // Check if canvas is scaled
          const scaleX = canvasRect.width / canvas.width;
          const scaleY = canvasRect.height / canvas.height;
          console.log('Canvas scaling:', {
            scaleX: scaleX,
            scaleY: scaleY,
            isScaled: scaleX !== 1 || scaleY !== 1
          });
        }
      }, 150);
    }
  }, [isOpen, signatureRef]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      data-modal-backdrop
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        overflow: 'auto'
      }}
      onClick={(e) => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        data-modal-content
        className="bg-white rounded-xl p-6 shadow-2xl"
        style={{ 
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium mb-4 text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#40E0D0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          {title}
        </h3>
        <div className="mb-4">
          <div className="border rounded relative" style={{ overflow: 'hidden', width: '100%', maxWidth: '500px' }}>
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas',
                style: { 
                  width: '500px',
                  height: '200px',
                  touchAction: 'none',
                  display: 'block'
                }
              }}
            />
            <button
              onClick={onClear}
              className="absolute top-2 right-2 p-1 bg-gray-100 rounded-full hover:bg-gray-200"
              title="Clear signature"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </div>
        <div className="flex justify-between items-center gap-2">
          {hasExistingSignature && onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              title="Delete existing signature"
            >
              <Trash2 size={16} />
              Delete Signature
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-[#2CB5A5] text-white rounded-md hover:bg-[#2CB5A5]/70"
            >
              Save Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal at document.body level using portal
  return ReactDOM.createPortal(modalContent, document.body);
};

export default SignatureModal; 